import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { AppProps } from "next/app";
import { FC, ReactElement } from "react";
import { Provider } from "react-redux";
import { expertModeSlice } from "../../features/expertMode/expertModeSlice";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { store } from "../../store";
import SPlusViewPage from "./SPlusViewPage";
import imageResize from "../../../public/img/resize.svg"
import ExpertModePage from "./ExpertModePage";
import { defaultExpertModeTheme } from "../../theme/expert-mode";

const ExpertLayer = styled.div([{
    position: "fixed",
    width: "100%",
    height: "100%",
    zIndex: 1,
    overflow: "scroll"
}])

const StandardLayer = styled.div<{ expertModeIsEnabled: boolean }>([
    {
        position: "fixed",
        backgroundColor: "white",
        zIndex: 2,
        width: "100%",
        height: "100%",
        transition: "transform 0.5s",
        borderRadius: "40px"
    },
    (props) =>
        props.expertModeIsEnabled ?
            {
                transform: "translateX(30%) translateY(30%) scale(0.3) "
            }
            :
            {
                transform: "translateX(0%) translateY(0%) scale(1)",
            }
])

const Controls = styled.div<{ expertModeIsEnabled: boolean }>([
    {
        position: "absolute",
        backgroundColor: "white",
        width: 200,
        height: 200,
        right: 60,
        top: 60,
        borderRadius: 40,
        transition: "opacity 0.5s",
        zIndex: 10001,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer"
    },
    (props) =>
        props.expertModeIsEnabled ?
            {
                opacity: 1
            }
            :
            {
                opacity: 0
            }
])

const Overlay = styled.div<{ expertModeIsEnabled: boolean }>([
    (props) => [
        css`
            display: block;
            position: fixed;
            padding: 100px 0;
            right: 0;
            bottom: 0;
            top: 0;
            left: 0;
            background-color: black;
            pointer-events: none;
            transition: opacity 0.5s;
            border-radius: 40px;
            overflow: hidden;
            z-index: 10000;
        `,
        props.expertModeIsEnabled
            ?
            css`
                opacity: 0.5;
            `
            :
            css`
                opacity: 0;
            `
    ]
])

const OverlayWrap = styled.div<{ expertModeIsEnabled: boolean }>([
    (props) => [
        css`
            overflow: hidden;
            transition: border-radius 0.5s; 
        `,
        props.expertModeIsEnabled
            ?
            css`
              border-radius: 40px;
            `
            :
            css`
              border-radius: 0px;
            `
    ]
])

const Img = styled.img(() => [
    css`
        width: 100px;
        height: 100px;
        fill: black;
    `
])

const Main: FC<AppProps> = ({ Component, pageProps, router }): ReactElement => {
    const state = useAppSelector(state => state.expertMode);
    const dispatch = useAppDispatch();

    const expertModeIsEnabled = state.enabled

    return <>
        <ExpertLayer>
            <ExpertModePage expertTheme={defaultExpertModeTheme} />
        </ExpertLayer>
        <StandardLayer {...{ expertModeIsEnabled }}>
            <OverlayWrap  {...{ expertModeIsEnabled }}>
                <Overlay {...{ expertModeIsEnabled }} />
                <SPlusViewPage pageProps={pageProps} Component={Component} router={router} />
            </OverlayWrap>
            <Controls onClick={() => dispatch(expertModeSlice.actions.disable())} {...{ expertModeIsEnabled }}>
                <Img src={imageResize.src} />
            </Controls>
        </StandardLayer>
    </>
}

const AngacomPage = (props: AppProps): ReactElement => {
    return (
        <Provider store={store}>
            <Main {...props} />
        </Provider>
    );
}

export default AngacomPage;